import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Minus, Plus, Trash2, Store, ShoppingBag } from "lucide-react";
import { rupee, rupee2 } from "../lib/format";
import { useCart } from "../context/CartContext";
import { VegBadge } from "../components/customer/ItemCard";

export default function Cart() {
  const navigate = useNavigate();
  const { grouped, totals, changeQty, removeLine, lineTotal, table } = useCart();

  if (totals.count === 0) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-[#08183A] flex flex-col items-center justify-center p-6">
        <ShoppingBag size={48} className="text-slate-700" />
        <p className="text-slate-400 mt-4">Your food court cart is empty</p>
        <button onClick={() => navigate("/order")} className="gold-btn rounded-xl px-6 py-2.5 mt-5 text-sm" data-testid="browse-menu-btn">Browse Menu</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08183A] pb-40">
      <div className="sticky top-0 z-50 glass px-4 py-3 flex items-center gap-2">
        <button onClick={() => navigate("/order")} className="h-8 w-8 rounded-full card-surface-2 flex items-center justify-center" data-testid="cart-back-btn"><ChevronLeft size={18} /></button>
        <div>
          <div className="font-display font-bold text-lg">Your Food Court Order</div>
          <div className="text-[11px] text-slate-400">Table {table?.table_code} · {totals.restaurants} restaurant{totals.restaurants > 1 ? "s" : ""}</div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {grouped.map((r) => (
          <div key={r.restaurant_id} className="card-surface rounded-2xl overflow-hidden" data-testid={`cart-group-${r.restaurant_id}`}>
            <div className="flex items-center gap-2 px-4 py-3 border-b hairline bg-[#16336B]">
              <Store size={15} className="text-amber-400" />
              <span className="font-semibold text-sm">{r.restaurant_name}</span>
              <span className="ml-auto text-xs text-slate-400">{rupee(r.subtotal)}</span>
            </div>
            <div className="divide-y divide-white/5">
              {r.lines.map((l) => (
                <div key={l.key} className="flex items-center gap-3 px-4 py-3">
                  <VegBadge type={l.food_type} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    {(l.variant || l.addons?.length > 0) && (
                      <div className="text-[11px] text-slate-500 truncate">
                        {l.variant?.name}{l.addons?.length ? ` · ${l.addons.map((a) => a.name).join(", ")}` : ""}
                      </div>
                    )}
                    <div className="text-xs text-slate-400 mt-0.5">{rupee2(lineTotal(l))}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 card-surface-2 rounded-lg px-1 py-1">
                      <button onClick={() => changeQty(l.key, -1)} className="px-1.5 text-amber-400" data-testid={`cart-dec-${l.key}`}><Minus size={13} /></button>
                      <span className="text-sm font-bold w-4 text-center">{l.qty}</span>
                      <button onClick={() => changeQty(l.key, 1)} className="px-1.5 text-amber-400" data-testid={`cart-inc-${l.key}`}><Plus size={13} /></button>
                    </div>
                    <button onClick={() => removeLine(l.key)} className="text-slate-600 hover:text-red-400" data-testid={`cart-remove-${l.key}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* bill */}
        <div className="card-surface rounded-2xl p-4 space-y-2 text-sm">
          <Row label="Food Total" value={rupee2(totals.food)} />
          <Row label="Taxes (GST 5%)" value={rupee2(totals.tax)} />
          <Row label={`Packaging (${totals.restaurants} × ₹15)`} value={rupee2(totals.packaging)} />
          <Row label="Convenience Fee" value={rupee2(totals.convenience)} />
          <div className="border-t hairline pt-2 flex justify-between font-display font-extrabold text-lg">
            <span>Payable</span><span className="gold-text">{rupee2(totals.payable)}</span>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md p-4 glass">
        <button onClick={() => navigate("/checkout")} className="gold-btn w-full rounded-xl py-3.5 flex items-center justify-center gap-2 text-base" data-testid="proceed-checkout-btn">
          Proceed to Pay · {rupee(totals.payable)}
        </button>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-slate-300"><span>{label}</span><span>{value}</span></div>
);
